import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

interface AnalyticsData {
  period: string;
  summary: {
    total_submissions: number;
    successful_sends: number;
    failed_sends: number;
    spam_blocked: number;
    connected_chats: number;
  };
  daily_stats: Array<{
    date: string;
    total_submissions: number;
    successful_sends: number;
    failed_sends: number;
    spam_blocked: number;
    unique_ips: number;
  }>;
}

interface StatCardProps {
  label: string;
  value: number;
  color: string;
}

function StatCard({ label, value, color }: StatCardProps) {
  return (
    <div className={`p-4 rounded-lg border ${color}`}>
      <p className="text-2xl font-bold">{value.toLocaleString()}</p>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );
}

const periods = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
];

interface FormAnalyticsProps {
  formId: string;
}

export function FormAnalytics({ formId }: FormAnalyticsProps) {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [period, setPeriod] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, [formId, period]);

  const loadAnalytics = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await api.getFormAnalytics(formId, period);
      setAnalytics(data as AnalyticsData);
    } catch (err) {
      setError('Failed to load analytics');
      console.error('Analytics error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTooltipDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-gray-500">
            Loading analytics...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !analytics) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-gray-500">
            {error || 'No analytics data available'}
          </div>
        </CardContent>
      </Card>
    );
  }

  const { summary, daily_stats } = analytics;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Analytics</CardTitle>
          <div className="flex gap-1">
            {periods.map((p) => (
              <Button
                key={p.value}
                variant={period === p.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Submissions"
            value={summary.total_submissions}
            color="bg-blue-50 border-blue-200"
          />
          <StatCard
            label="Delivered"
            value={summary.successful_sends}
            color="bg-green-50 border-green-200"
          />
          <StatCard
            label="Failed"
            value={summary.failed_sends}
            color="bg-red-50 border-red-200"
          />
          <StatCard
            label="Connected Chats"
            value={summary.connected_chats}
            color="bg-purple-50 border-purple-200"
          />
        </div>

        {/* Chart */}
        {daily_stats && daily_stats.length > 0 ? (
          <div className="pt-4">
            <p className="text-sm font-medium text-gray-700 mb-4">
              Submissions over time
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={daily_stats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                  allowDecimals={false}
                />
                <Tooltip
                  labelFormatter={formatTooltipDate}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="total_submissions"
                  name="Submissions"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 0, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-500 border rounded-lg">
            No submission data for this period
          </div>
        )}
      </CardContent>
    </Card>
  );
}
